import { useEffect, useMemo, useState } from "react";
import { Smartphone, Search, Users, CheckSquare, BarChart3, Fingerprint, ShieldOff, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Staff app features an admin or manager can switch on per person. Attendance is always on.
const GROUPS = [
  {
    id: "leads", title: "Leads", icon: Users, view: "app_leads_view",
    perms: [
      { id: "app_leads_view", label: "View" },
      { id: "app_leads_edit", label: "Edit" },
      { id: "app_leads_remarks", label: "Remarks" },
      { id: "app_leads_followup", label: "Follow-up" },
      { id: "app_leads_create", label: "Add" },
    ],
  },
  {
    id: "tasks", title: "Tasks", icon: CheckSquare, view: "app_tasks_view",
    perms: [
      { id: "app_tasks_view", label: "View" },
      { id: "app_tasks_update", label: "Status" },
      { id: "app_tasks_comment", label: "Comments" },
      { id: "app_tasks_create", label: "Add" },
    ],
  },
  { id: "reports", title: "Reports", icon: BarChart3, view: "app_reports_view", perms: [{ id: "app_reports_view", label: "View" }] },
];
const ROLE_LABEL = { admin: "Admin", manager: "Manager", employee: "Employee", project_manager: "Project Manager" };

export default function StaffAppAccess() {
  const { businessId } = useBusiness();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const q = businessId ? `?business_id=${businessId}` : "";
      const r = await fetch(`${BACKEND_URL}/api/staff-app-access${q}`, { credentials: "include" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Could not load staff");
      setData(j);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [businessId]);

  const save = async (user, nextPerms, message) => {
    setSaving(user.user_id);
    try {
      const r = await fetch(`${BACKEND_URL}/api/staff-app-access/${user.user_id}`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ permissions: nextPerms, business_id: data?.business_id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Could not save");
      setData((d) => ({ ...d, users: d.users.map((u) => (u.user_id === user.user_id ? { ...u, permissions: j.permissions } : u)) }));
      toast.success(message || `Saved for ${user.name}`);
    } catch (e) { toast.error(e.message); } finally { setSaving(null); }
  };

  const toggle = (user, group, permId) => {
    const p = { ...user.permissions, [permId]: !user.permissions[permId] };
    // No actions without View: turning View off clears the group, turning an action on needs View.
    if (permId === group.view && !p[permId]) group.perms.forEach((x) => { p[x.id] = false; });
    save(user, p);
  };
  const fullTasks = (user) => save(user, { ...user.permissions, app_tasks_view: true, app_tasks_update: true, app_tasks_comment: true, app_tasks_create: true }, `Full Tasks access turned on for ${user.name}`);
  const fullLeads = (user) => save(user, { ...user.permissions, app_leads_view: true, app_leads_edit: true, app_leads_remarks: true, app_leads_followup: true, app_leads_create: true }, `Full Leads access turned on for ${user.name}`);

  const users = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.users || []).filter((u) => !q || [u.name, u.email, u.role, u.designation].some((v) => v && String(v).toLowerCase().includes(q)));
  }, [data, search]);
  const counts = useMemo(() => {
    const all = data?.users || [];
    return GROUPS.map((g) => ({ ...g, n: all.filter((u) => u.permissions[g.view]).length }));
  }, [data]);

  if (error && !data) {
    return (
      <div className="max-w-xl mx-auto mt-16 text-center space-y-3" data-testid="staff-app-access-error">
        <ShieldOff className="w-10 h-10 mx-auto text-slate-400" />
        <p className="text-slate-700 dark:text-slate-200 font-medium">{error}</p>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-app-access">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><Smartphone className="w-6 h-6 text-indigo-600" />Staff App Access</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Choose which features each person can use in the Dnexus Staff app. Attendance is always on; everything else is off until you turn it on.
            Staff only see their own leads, tasks and reports.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><Fingerprint className="w-5 h-5 text-green-600" /><div><p className="text-xs text-slate-500">Attendance</p><p className="font-semibold">Everyone</p></div></CardContent></Card>
        {counts.map((g) => {
          const Icon = g.icon;
          return (
            <Card key={g.id}><CardContent className="p-4 flex items-center gap-3"><Icon className="w-5 h-5 text-indigo-600" /><div><p className="text-xs text-slate-500">{g.title}</p><p className="font-semibold">{g.n} {g.n === 1 ? "person" : "people"}</p></div></CardContent></Card>
          );
        })}
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff…" className="pl-9" data-testid="staff-app-search" />
      </div>

      {loading && !data ? (
        <p className="text-slate-500">Loading staff…</p>
      ) : users.length === 0 ? (
        <p className="text-slate-500">No staff found.</p>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <Card key={u.user_id} className={u.editable ? "" : "opacity-70"} data-testid={`staff-row-${u.user_id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white flex items-center justify-center font-medium shrink-0">{u.name?.charAt(0)?.toUpperCase() || "U"}</div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">{u.name}</p>
                      <p className="text-xs text-slate-500 truncate">{[u.designation, u.email].filter(Boolean).join(" · ")}</p>
                    </div>
                    <Badge variant="secondary">{ROLE_LABEL[u.role] || u.role}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                  {u.editable ? (
                    <Button size="sm" variant="outline" disabled={saving === u.user_id || (u.permissions.app_leads_view && u.permissions.app_leads_edit && u.permissions.app_leads_remarks && u.permissions.app_leads_followup && u.permissions.app_leads_create)}
                      onClick={() => fullLeads(u)} data-testid={`full-leads-${u.user_id}`}>
                      Give full Leads access
                    </Button>
                  ) : null}
                  {u.editable ? (
                    <Button size="sm" variant="outline" disabled={saving === u.user_id || (u.permissions.app_tasks_view && u.permissions.app_tasks_update && u.permissions.app_tasks_comment && u.permissions.app_tasks_create)}
                      onClick={() => fullTasks(u)} data-testid={`full-tasks-${u.user_id}`}>
                      Give full Tasks access
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-500">Only {u.role === "admin" ? "a super admin" : "an admin"} can change this person</span>
                  )}
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  {GROUPS.map((g) => {
                    const Icon = g.icon;
                    const viewOn = u.permissions[g.view];
                    return (
                      <div key={g.id} className={`rounded-lg border p-3 ${viewOn ? "border-green-200 bg-green-50/60 dark:bg-green-900/10" : "border-slate-200 dark:border-slate-700"}`}>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2 mb-2"><Icon className="w-4 h-4" />{g.title}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                          {g.perms.map((p) => {
                            const locked = p.id !== g.view && !viewOn;
                            return (
                              <label key={p.id} className={`flex items-center gap-2 text-sm ${locked ? "text-slate-400" : "text-slate-700 dark:text-slate-300"}`}>
                                <Switch checked={!!u.permissions[p.id] && !locked} disabled={!u.editable || locked || saving === u.user_id}
                                  onCheckedChange={() => toggle(u, g, p.id)} data-testid={`sw-${u.user_id}-${p.id}`} />
                                {p.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
